import type { CSSProperties, ComponentProps, ReactNode } from 'react'
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import type { Map as MapboxMapInstance } from 'mapbox-gl'
import type { MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox'
import Map, { NavigationControl } from 'react-map-gl/mapbox'

import { EnsureMapboxLoaded, useMapboxContext } from 'contexts/MapboxContext'
import { createMapboxMapHandle } from 'lib/maps/createMapboxMapHandle'
import type { MapHandle, MapViewport } from 'lib/maps/types'
import { MapboxAttributionControl } from './MapboxAttributionControl'
import { MapboxGlobalStyles } from './MapboxGlobalStyles'

const DEFAULT_MAP_STYLE = 'mapbox://styles/mapbox/streets-v12'
export type NavigationControlPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
export type MapboxInitialViewport = NonNullable<ComponentProps<typeof Map>['initialViewState']>
const DEFAULT_CONTAINER_STYLE: CSSProperties = {
  width: '100%',
  height: '100%',
}

export interface MapboxMapMoveEvent {
  handle: MapHandle
  viewport: MapViewport
  originalEvent?: Event
}

export interface MapboxMapProps extends Omit<
  ComponentProps<typeof Map>,
  'mapboxAccessToken' | 'initialViewState' | 'style' | 'onLoad' | 'onMove' | 'onMoveStart' | 'onMoveEnd'
> {
  initialViewport?: MapboxInitialViewport
  containerStyle?: CSSProperties
  showNavigationControl?: boolean
  navigationControlPosition?: NavigationControlPosition
  loadingElement?: ReactNode
  onLoad?: (handle: MapHandle) => void
  onMapReady?: (map: MapboxMapInstance) => void
  onMove?: (event: MapboxMapMoveEvent) => void
  onMoveStart?: (event: MapboxMapMoveEvent) => void
  onMoveEnd?: (event: MapboxMapMoveEvent) => void
  showAttributionControl?: boolean
}

function toMapViewport(event: ViewStateChangeEvent): MapViewport {
  return {
    longitude: event.viewState.longitude,
    latitude: event.viewState.latitude,
    zoom: event.viewState.zoom,
    pitch: event.viewState.pitch,
    bearing: event.viewState.bearing,
  }
}

export const MapboxMap = forwardRef<MapHandle, MapboxMapProps>(function MapboxMap(
  {
    children,
    mapStyle = DEFAULT_MAP_STYLE,
    initialViewport,
    containerStyle = DEFAULT_CONTAINER_STYLE,
    showNavigationControl = true,
    navigationControlPosition = 'bottom-right',
    loadingElement = null,
    onLoad,
    onMapReady,
    onMove,
    onMoveStart,
    onMoveEnd,
    showAttributionControl = true,
    ...restProps
  },
  ref,
) {
  const { accessToken } = useMapboxContext()
  const mapRef = useRef<MapRef | null>(null)
  const intl = useIntl()

  const getMapHandle = useCallback(() => {
    const map = mapRef.current?.getMap()
    return map ? createMapboxMapHandle(map) : undefined
  }, [])

  useImperativeHandle(ref, (): MapHandle => ({
    getBounds: () => getMapHandle()?.getBounds(),
    getZoom: () => getMapHandle()?.getZoom() ?? 0,
    fitBounds: (bounds, options) => getMapHandle()?.fitBounds(bounds, options),
    setViewport: (viewport, options) => getMapHandle()?.setViewport(viewport, options),
    panBy: (offset, options) => getMapHandle()?.panBy(offset, options),
    project: (coordinate) => getMapHandle()?.project(coordinate) ?? { x: 0, y: 0 },
    unproject: (point) => getMapHandle()?.unproject(point) ?? { lat: 0, lng: 0 },
    getContainer: () => getMapHandle()?.getContainer() ?? null,
  }), [getMapHandle])

  const callMoveHandler = useCallback((
    handler: ((event: MapboxMapMoveEvent) => void) | undefined,
    event: ViewStateChangeEvent,
  ) => {
    if (!handler) {
      return
    }

    const handle = getMapHandle()
    if (!handle) {
      return
    }

    handler({
      handle,
      viewport: toMapViewport(event),
      originalEvent: (event as { originalEvent?: Event }).originalEvent,
    })
  }, [getMapHandle])

  const handleLoad = useCallback(() => {
    const map = mapRef.current?.getMap()
    const handle = getMapHandle()
    if (map) {
      onMapReady?.(map)
    }
    if (handle) {
      onLoad?.(handle)
    }
  }, [getMapHandle, onLoad, onMapReady])

  return (
    <EnsureMapboxLoaded loadingElement={loadingElement}>
      <MapboxGlobalStyles />
      <Map
        ref={mapRef}
        mapboxAccessToken={accessToken}
        initialViewState={initialViewport}
        mapStyle={mapStyle}
        style={containerStyle}
        onLoad={handleLoad}
        onMove={(event) => callMoveHandler(onMove, event)}
        onMoveStart={(event) => callMoveHandler(onMoveStart, event)}
        onMoveEnd={(event) => callMoveHandler(onMoveEnd, event)}
        language={intl.locale}
        {...restProps}
        attributionControl={false}
      >
        {children}
        {showAttributionControl && <MapboxAttributionControl />}
        {showNavigationControl && <NavigationControl position={navigationControlPosition} showCompass={false} />}
      </Map>
    </EnsureMapboxLoaded>
  )
})
